import React, { useMemo } from 'react';

import { TimelineCanvas } from './ui';
import { timelineTheme } from './theme';
import { useTimelineActions } from '../hooks/useTimelineActions';
import { transformCycles } from '../utils/helpers/cycle-helpers';
import { hasSameSideOverlap } from '../utils/overlap-helpers';

import type { CycleData, TimelineProps } from '../types';
import { TimelineCard, TimelineWrapper } from './styles';

function isSelectionBlocked(selectedCycleIds: string[], selectionPool: CycleData[]): boolean {
	const selectedCycles = selectionPool.filter((cycle) => selectedCycleIds.includes(cycle.id));
	const isBlocked = hasSameSideOverlap(selectedCycles);

	return isBlocked;
}

function stampSelection(cycles: CycleData[], selectedCycleIds: string[], canEdit: boolean): CycleData[] {
	const stampedCycles = cycles.map((cycle) => {
		if (!selectedCycleIds.includes(cycle.id)) {
			return cycle;
		}

		return { ...cycle, selected: true, editable: canEdit };
	});

	return stampedCycles;
}

export function Timeline({
	value,
	onChange,
	videoDuration,
	currentTime = 0,
	theme = timelineTheme,
	onValidationError,
	onInteractionError,
	selectedCycleId,
	selectedCycleIds,
	onCycleSelect,
	onSeek,
	isEditMode = false,
	enableBilateralMirroring = false,
	allCycles,
	onPauseVideo,
	onScrubbingChange,
	onCycleDragChange,
	onCycleHover,
	bilateralDragPreview,
	onBilateralDragPreviewChange,
	allowOverlap = false,
	overlapColors,
	outOfRangeCycleIds,
	outOfRangeFromTime,
	highlightedCycleIds
}: Readonly<TimelineProps>) {
	const filteredCycles = useMemo(() => {
		return value;
	}, [value]);

	const { cycles, handleUpdateCyclePosition, handleSelectCycle } = useTimelineActions(
		filteredCycles,
		onChange,
		videoDuration,
		onValidationError,
		onCycleSelect,
		true,
		allCycles,
		allowOverlap
	);

	const allCyclesData = useMemo(() => {
		const allCyclesTransformed = allCycles ? transformCycles(allCycles) : undefined;

		return allCyclesTransformed;
	}, [allCycles]);

	const renderCycles = useMemo(() => {
		if (!selectedCycleIds || selectedCycleIds.length === 0) {
			return cycles;
		}

		const isBlocked = isSelectionBlocked(selectedCycleIds, allCyclesData ?? cycles);
		const stampedCycles = stampSelection(cycles, selectedCycleIds, !isBlocked);

		return stampedCycles;
	}, [cycles, allCyclesData, selectedCycleIds]);

	const currentCycleData = selectedCycleId ? cycles.find((c) => c.id === selectedCycleId) || null : null;

	if (!videoDuration || videoDuration <= 0) {
		return null;
	}

	return (
		<TimelineCard data-testid="timeline">
			<TimelineWrapper>
				<TimelineCanvas
					cycles={renderCycles}
					currentCycle={currentCycleData}
					videoDuration={videoDuration}
					currentTime={currentTime}
					theme={theme}
					onCycleSelect={handleSelectCycle}
					onUpdateCyclePosition={handleUpdateCyclePosition}
					onInteractionError={onInteractionError}
					onSeek={onSeek}
					isEditMode={isEditMode}
					allowBilateral={enableBilateralMirroring}
					onPauseVideo={onPauseVideo}
					onScrubbingChange={onScrubbingChange}
					onCycleDragChange={onCycleDragChange}
					onCycleHover={onCycleHover}
					allCycles={allCyclesData}
					bilateralDragPreview={bilateralDragPreview}
					onBilateralDragPreviewChange={onBilateralDragPreviewChange}
					allowOverlap={allowOverlap}
					overlapColors={overlapColors}
					outOfRangeCycleIds={outOfRangeCycleIds}
					outOfRangeFromTime={outOfRangeFromTime}
					highlightedCycleIds={highlightedCycleIds}
				/>
			</TimelineWrapper>
		</TimelineCard>
	);
}
